import React from 'react';
import { Grid, Button, Box, Typography, Card, CardContent } from '@mui/material';
import { Add as AddIcon, Group as GroupIcon } from '@mui/icons-material';
import { AuditionRoleCard, IAuditionRoleCard } from './AuditionRoleCard';
import { IRole } from '../../../types/global/AuditionDetailsEditor.types';
import GenericModalWrapper, { IGenericModalWrapper } from '../GenericModalWrapper';
import { useThemeContext } from '../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { theme } from '../../../constants/theme';

interface IAuditionRoleGrid extends IGenericModalWrapper, Omit<IAuditionRoleCard, 'role'> {
    roles?: IRole[];
    onAdd: () => void;
    isEditMode?: boolean;
    onDeleteRole?: (role: IRole, index: number) => void;
    onEditRole?: (role: IRole, index: number) => void;
}

// Individual Role Card Component

// Add Role Card Component
const AddRoleCard: React.FC<{ onAdd: () => void }> = ({ onAdd }) => {
    const { themeStyle } = useThemeContext();

    return (
        <Card
            sx={{
                width: '100%',
                border: `2px dashed ${theme[themeStyle].tag_white}`,
                borderRadius: 2,
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: theme[themeStyle].highlight,
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)',
                },
            }}
            onClick={onAdd}>
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    textAlign: 'center',
                }}>
                <Box
                    sx={{
                        backgroundColor: `${theme[themeStyle].highlight}20`,
                        borderRadius: '50%',
                        p: 2,
                        mb: 2,
                    }}>
                    <AddIcon
                        sx={{
                            fontSize: 32,
                            color: theme[themeStyle].highlight,
                        }}
                    />
                </Box>
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: theme[themeStyle].tag_white,
                        mb: 1,
                    }}>
                    Add New Role
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: theme[themeStyle].light_gray_text,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    }}>
                    Create a new role
                </Typography>
            </CardContent>
        </Card>
    );
};

// Empty State Component
const EmptyState: React.FC<{ onAdd: () => void }> = ({ onAdd }) => {
    const { themeStyle } = useThemeContext();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
            }}>
            <Box
                sx={{
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                    borderRadius: '50%',
                    p: 3,
                    mb: 3,
                }}>
                <GroupIcon
                    sx={{
                        fontSize: 48,
                        color: theme[themeStyle].light_gray_text,
                    }}
                />
            </Box>
            <Typography
                variant="h5"
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    color: theme[themeStyle].tag_white,
                    mb: 1,
                }}>
                No Roles Yet
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: theme[themeStyle].light_gray_text,
                    mb: 4,
                    maxWidth: 400,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                }}>
                Get started by creating your first position or audition role. You can add details like age range,
                experience level, and requirements.
            </Typography>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onAdd}
                sx={{
                    backgroundColor: theme[themeStyle].highlight,
                    color: theme[themeStyle].black,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    '&:hover': {
                        backgroundColor: theme[themeStyle].primary_orange,
                    },
                }}>
                Add Your First Role
            </Button>
        </Box>
    );
};

const AuditionRoleGrid: React.FC<IAuditionRoleGrid> = ({
    roles,
    onAdd,
    isEditMode,
    onDeleteRole,
    onEditRole,
    ...props
}) => {
    return (
        <GenericModalWrapper {...props}>
            <Box sx={{ width: '100%', p: 3, height: '100%' }}>
                {!roles || roles.length === 0 ? (
                    <EmptyState onAdd={onAdd} />
                ) : (
                    <Grid container spacing={3}>
                        {roles.map((role: IRole, index: number) => (
                            <Grid item xs={12} key={role.id || index}>
                                <AuditionRoleCard
                                    role={role}
                                    editMode={isEditMode}
                                    {...props}
                                    onDelete={(role: IRole) => onDeleteRole?.(role, index)}
                                    onEdit={(role: IRole) => onEditRole?.(role, index)}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <AddRoleCard onAdd={onAdd} />
                        </Grid>
                    </Grid>
                )}
            </Box>
        </GenericModalWrapper>
    );
};

export { AuditionRoleGrid };
